import React, { useMemo, useState } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Chip } from '@/components/UI/Chip';
import { StatCard } from '@/components/UI/StatCard';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Alert } from '@/components/UI/Alert';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetAiBillingQuery,
  useLazyGetAiBillingQuery,
  type AiBillingProvider,
  type AiBillingRange,
  type AiBillingRawCall,
  type AiBillingTotals,
} from '@/redux/internal-services/platformAdminApi';

const RANGES: { id: AiBillingRange; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: 'month', label: 'This month' },
  { id: 'custom', label: 'Custom' },
];
const AUTO_REFRESH_MS = 5 * 60 * 1000;   // served from the server cache; the providers are not hit more often than that

const CONNECTION: Record<string, { label: string; variant: BadgeProps['variant'] }> = {
  connected: { label: 'Connected', variant: 'success' },
  invalid_credentials: { label: 'Key rejected', variant: 'danger' },
  not_configured: { label: 'Not configured', variant: 'neutral' },
  rate_limited: { label: 'Rate limited', variant: 'warning' },
  unavailable: { label: 'Unavailable', variant: 'warning' },
  error: { label: 'Error', variant: 'danger' },
};

const fmt = (n: number | undefined | null) => (n ?? 0).toLocaleString();
const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
/** The provider query window is in UTC days - show it in UTC, not the browser's timezone. */
const utc = (iso?: string | null) => (iso ? `${new Date(iso).toLocaleString('en-US', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' })} UTC` : '—');

function statusBadge(c: AiBillingRawCall): { text: string; variant: BadgeProps['variant'] } {
  if (c.status === null) return { text: 'No response', variant: 'danger' };
  if (c.ok) return { text: `${c.status} OK`, variant: 'success' };
  if (c.status === 401 || c.status === 403) return { text: `${c.status} Permission needed`, variant: 'warning' };
  return { text: String(c.status), variant: 'danger' };
}

const RawCallRow: React.FC<{ call: AiBillingRawCall }> = ({ call }) => {
  const [open, setOpen] = useState(false);
  const s = statusBadge(call);
  return (
    <div className="border-t border-ui-border first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="focus-ring flex w-full flex-wrap items-center gap-2 px-4 py-2.5 text-left hover:bg-ui-hover"
      >
        <Badge variant={s.variant}>{s.text}</Badge>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ui-text">{call.name}</span>
        <span className="text-caption text-ui-text3">{call.elapsed_ms} ms · {call.authenticated_with}</span>
        <span className="text-ui-text3">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="space-y-2 px-4 pb-3">
          <p className="break-all font-mono text-caption text-ui-text3">{call.method} {call.url}</p>
          <pre className="no-scrollbar max-h-80 overflow-auto rounded-control border border-ui-border bg-ui-subtle p-3 text-xs text-ui-text">{call.body}</pre>
          <p className="text-caption text-ui-text3">
            Raw provider response, fetched {when(call.fetched_at)}{call.truncated ? ' (truncated)' : ''}
            {call.request_id ? ` · request ${call.request_id}` : ''} ·{' '}
            <a className="text-accent-text underline" href={call.doc} target="_blank" rel="noreferrer">API reference</a>
          </p>
        </div>
      )}
    </div>
  );
};

const AccessNeeded: React.FC<{ p: AiBillingProvider }> = ({ p }) => {
  const needs = useMemo(() => {
    const seen = new Map<string, { credential: string; env: string; how: string; metrics: string[] }>();
    Object.entries(p.metrics).forEach(([name, m]) => {
      if (m.requires.env === '-' ) return;
      const k = m.requires.env;
      const e = seen.get(k) ?? { ...m.requires, metrics: [] };
      e.metrics.push(name.replace('_', ' '));
      seen.set(k, e);
    });
    return [...seen.values()];
  }, [p.metrics]);
  const noApi = Object.entries(p.metrics).filter(([, m]) => m.requires.env === '-' && m.requires.credential.startsWith('None'));
  if (p.billing_credential_configured && needs.length === 0 && noApi.length === 0) return null;
  return (
    <div className="space-y-2 px-4 py-3">
      <p className="eyebrow">Not available through API</p>
      {!p.billing_credential_configured && needs.map((n) => (
        <p key={n.env} className="text-sm text-ui-text2">
          <span className="font-medium text-ui-text">{n.metrics.join(', ')}</span> need: {n.credential}. Set <code className="font-mono text-xs">{n.env}</code> on
          the backend. {n.how}
        </p>
      ))}
      {noApi.map(([name, m]) => (
        <p key={name} className="text-sm text-ui-text2">
          <span className="font-medium text-ui-text">{name.replace('_', ' ')}:</span> {m.reason}
        </p>
      ))}
    </div>
  );
};

const ProviderCard: React.FC<{ p: AiBillingProvider }> = ({ p }) => {
  const conn = CONNECTION[p.connection.status] ?? { label: p.connection.status, variant: 'neutral' as const };
  const account = p.connection.account ?? {};
  const rec = p.recorded;
  return (
    <section className="card-surface overflow-hidden" aria-label={`${p.label} billing`}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 pt-4">
        <div className="min-w-0">
          <h2 className="text-h3 text-ui-text">{p.label}</h2>
          <p className="text-caption text-ui-text3">{p.connection.detail}</p>
        </div>
        <div className="flex items-center gap-2">
          {p.billing_credential_configured && <Badge variant="accent">Billing credential set</Badge>}
          <Badge variant={conn.variant}>{conn.label}</Badge>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 px-4 py-3 text-sm sm:grid-cols-2">
        {Object.entries(account)
          .filter(([, v]) => typeof v === 'string' || typeof v === 'number')
          .map(([k, v]) => (
            <div key={k} className="min-w-0">
              <dt className="eyebrow">{k.replace(/_/g, ' ')}</dt>
              <dd className="truncate font-mono text-xs text-ui-text">{String(v)}</dd>
            </div>
          ))}
        <div>
          <dt className="eyebrow">Configured chat</dt>
          <dd className="font-mono text-xs text-ui-text">{p.configured?.chat ? `${p.configured.chat.provider} · ${p.configured.chat.model ?? 'agent default'}` : 'not used'}</dd>
        </div>
        <div>
          <dt className="eyebrow">Configured realtime voice</dt>
          <dd className="font-mono text-xs text-ui-text">{p.configured?.realtime ? `${p.configured.realtime.provider} · ${p.configured.realtime.model ?? 'default'}` : 'not used'}</dd>
        </div>
        {p.configured && p.configured.agent_rules > 0 && (
          <div>
            <dt className="eyebrow">Per-agent rules</dt>
            <dd className="text-xs text-ui-text">{p.configured.agent_rules} agent{p.configured.agent_rules === 1 ? '' : 's'} routed here</dd>
          </div>
        )}
      </dl>

      {rec && (
        <div className="border-t border-ui-border px-4 py-3">
          <p className="eyebrow mb-1">Recorded by this app (tokens as reported)</p>
          <p className="font-mono text-xs text-ui-text">
            {fmt(rec.runs)} runs · in {fmt(rec.input_tokens)} · out {fmt(rec.output_tokens)} · cached {fmt(rec.cached_tokens)}
            {rec.reasoning_tokens ? ` · reasoning ${fmt(rec.reasoning_tokens)}` : ''}
          </p>
          <p className="text-caption text-ui-text3">text {fmt(rec.text.runs)} runs · voice {fmt(rec.voice.runs)} runs</p>
        </div>
      )}

      <div className="border-t border-ui-border">
        <p className="eyebrow px-4 pt-3">Provider responses (raw)</p>
        {p.raw_calls.length === 0
          ? <p className="px-4 py-3 text-sm text-ui-text3">No calls were made ({p.connection.detail})</p>
          : p.raw_calls.map((c) => <RawCallRow key={c.name} call={c} />)}
      </div>
      <div className="border-t border-ui-border"><AccessNeeded p={p} /></div>
      <p className="border-t border-ui-border px-4 py-2 text-caption text-ui-text3">
        Last synced {when(p.synced_at)}{p.from_cache ? ' (cached)' : ''} · <a className="text-accent-text underline" href={p.docs} target="_blank" rel="noreferrer">provider docs</a>
      </p>
    </section>
  );
};

const TokensBars: React.FC<{ rows: Array<AiBillingTotals & { date: string }> }> = ({ rows }) => {
  const byDay = useMemo(() => {
    const m = new Map<string, number>();
    rows.forEach((r) => m.set(r.date, (m.get(r.date) ?? 0) + r.input_tokens + r.output_tokens));
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rows]);
  const max = Math.max(1, ...byDay.map(([, v]) => v));
  if (byDay.length === 0) return null;
  return (
    <div role="img" aria-label="Tokens per day" className="flex h-32 items-end gap-1">
      {byDay.map(([d, v]) => (
        <div key={d} className="group flex min-w-0 flex-1 flex-col items-center justify-end gap-1" title={`${d}: ${fmt(v)} tokens`}>
          <div className="w-full rounded-t bg-accent" style={{ height: `${Math.max(4, Math.round((v / max) * 100))}%` }} />
          <span className="truncate text-[10px] text-ui-text3">{d.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

const AiBillingPage: React.FC = () => {
  const [range, setRange] = useState<AiBillingRange>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState<string | null>(null);
  const args = { range, ...(range === 'custom' ? { date_from: from || undefined, date_to: to || undefined } : {}) };
  const skip = range === 'custom' && !from;
  const { data, isLoading, isFetching, isError, error, refetch } = useGetAiBillingQuery(args, { skip, pollingInterval: AUTO_REFRESH_MS });
  const [trigger] = useLazyGetAiBillingQuery();
  const report = data?.data;

  const refresh = async () => {
    setRefreshing(true);
    setRefreshNote(null);
    const started = Date.now();
    try {
      const res = await trigger({ ...args, refresh: true }).unwrap();
      const ps = res.data.providers;
      const cached = ps.every((p) => p.from_cache);
      setRefreshNote(cached
        ? `Providers were checked ${Math.max(...ps.map((p) => (p as { cache_age_s?: number }).cache_age_s ?? 0))}s ago - refresh is limited to once per ${res.data.limits.refresh_floor_s}s, so this is the latest data.`
        : 'Refreshed just now.');
      refetch();
    } catch {
      setRefreshNote('Could not refresh. Try again in a moment.');
    } finally {
      // keep the busy state visible long enough to notice even when the answer came from the cache
      setTimeout(() => setRefreshing(false), Math.max(0, 600 - (Date.now() - started)));
    }
  };
  const rec = report?.recorded;

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          title="AI Provider Usage & Billing"
          subtitle="What each provider's own API returns for usage, cost and credits (raw), plus the tokens this app recorded."
          toolbar={
            <div className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="eyebrow shrink-0">Range</span>
                <div role="group" aria-label="Date range" className="flex min-w-0 flex-wrap gap-2">
                  {RANGES.map((r) => (
                    <Chip key={r.id} selected={range === r.id} onClick={() => setRange(r.id)}>{r.label}</Chip>
                  ))}
                </div>
                <Button type="button" size="sm" variant="outline" className="sm:ml-auto" onClick={refresh} disabled={refreshing} aria-busy={refreshing}>
                  {refreshing ? 'Refreshing…' : 'Refresh now'}
                </Button>
              </div>
              {range === 'custom' && (
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1">
                    <span className="eyebrow">From</span>
                    <input type="date" className={fieldClass({ size: 'sm' })} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
                  </label>
                  <label className="flex flex-col gap-1">
                    <span className="eyebrow">To</span>
                    <input type="date" className={fieldClass({ size: 'sm' })} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
                  </label>
                </div>
              )}
              {report && (
                <p className="text-caption text-ui-text3">
                  Auto-synced every 5 minutes from a server cache (providers are called at most once per {report.limits.probe_ttl_s / 60} min;
                  manual refresh at most once per minute). Provider date window: {utc(report.range.from)} → {utc(report.range.to)}.
                  {refreshNote ? ` ${refreshNote}` : ''}
                </p>
              )}
            </div>
          }
        />
      }
    >
      {isError && <Alert tone="danger">{getErrorDetail(error) ?? 'Could not load AI billing.'}</Alert>}
      {skip && <EmptyState className="rounded-card border border-dashed border-ui-border py-12" title="Pick a start date" description="Choose a custom range to load it." />}

      {!skip && isLoading && <LoadingState label="Contacting providers…" className="py-16" />}

      {report && (
        <div className={cn('space-y-4', isFetching && 'opacity-70 transition-opacity')}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <StatCard label="Runs recorded" value={fmt(rec?.totals.runs)} />
            <StatCard label="Input tokens" value={fmt(rec?.totals.input_tokens)} />
            <StatCard label="Output tokens" value={fmt(rec?.totals.output_tokens)} />
            <StatCard label="Cached tokens" value={fmt(rec?.totals.cached_tokens)} />
            <StatCard label="Reasoning tokens" value={fmt(rec?.totals.reasoning_tokens)} />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {report.providers.map((p) => <ProviderCard key={p.provider} p={p} />)}
          </div>

          <section className="card-surface p-4" aria-label="Tokens recorded by this app">
            <h2 className="text-h3 text-ui-text">Recorded by this app</h2>
            <p className="mb-3 text-caption text-ui-text3">Tokens exactly as the providers reported them for chat turns and voice sessions in this range. No cost is calculated here.</p>
            {rec && rec.totals.runs === 0 ? (
              <EmptyState variant="inline" title="No usage recorded in this range." />
            ) : rec ? (
              <div className="space-y-5">
                <TokensBars rows={rec.by_day} />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(['text', 'voice'] as const).map((k) => (
                    <div key={k} className="rounded-control border border-ui-border p-3">
                      <p className="eyebrow">{k === 'text' ? 'Text chat / push-to-talk' : 'Interactive voice'}</p>
                      <p className="font-mono text-xs text-ui-text">
                        {fmt(rec.text_vs_voice[k].runs)} runs · in {fmt(rec.text_vs_voice[k].input_tokens)} · out {fmt(rec.text_vs_voice[k].output_tokens)}
                      </p>
                      {k === 'voice' && (
                        <p className="font-mono text-caption text-ui-text3">
                          audio in {fmt(rec.text_vs_voice.voice.audio_input_tokens)} · audio out {fmt(rec.text_vs_voice.voice.audio_output_tokens)} ·
                          text in {fmt(rec.text_vs_voice.voice.text_input_tokens)} · text out {fmt(rec.text_vs_voice.voice.text_output_tokens)}
                          {rec.text_vs_voice.voice.billable_audio_seconds ? ` · billable audio ${rec.text_vs_voice.voice.billable_audio_seconds}s (xAI)` : ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="table-card">
                  <div className="no-scrollbar overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <caption className="sr-only">Usage by provider and model</caption>
                      <thead><tr>
                        <th className="th-cell">Provider</th><th className="th-cell">Model</th>
                        <th className="th-cell text-right">Runs</th><th className="th-cell text-right">Input</th>
                        <th className="th-cell text-right">Output</th><th className="th-cell text-right">Cached</th>
                        <th className="th-cell text-right">Reasoning</th>
                      </tr></thead>
                      <tbody>
                        {rec.by_model.map((m) => (
                          <tr key={`${m.vendor}:${m.model}`} className="tr-hover">
                            <td className="td-cell text-sm">{m.vendor}</td>
                            <td className="td-cell whitespace-nowrap font-mono text-xs">{m.model}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(m.runs)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(m.input_tokens)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(m.output_tokens)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(m.cached_tokens)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(m.reasoning_tokens)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="table-card">
                  <div className="no-scrollbar overflow-x-auto">
                    <table className="w-full min-w-[760px]">
                      <caption className="sr-only">Recent runs</caption>
                      <thead><tr>
                        <th className="th-cell">When</th><th className="th-cell">Provider</th><th className="th-cell">Model</th>
                        <th className="th-cell">Mode</th><th className="th-cell text-right">Calls</th>
                        <th className="th-cell text-right">Input</th><th className="th-cell text-right">Output</th>
                        <th className="th-cell text-right">Cached</th>
                      </tr></thead>
                      <tbody>
                        {rec.recent_runs.map((r) => (
                          <tr key={r.run_id} className="tr-hover">
                            <td className="td-cell whitespace-nowrap text-caption text-ui-text3">{when(r.at)}</td>
                            <td className="td-cell text-sm">{r.provider ?? 'unknown'}</td>
                            <td className="td-cell whitespace-nowrap font-mono text-xs">{r.model ?? 'unknown'}</td>
                            <td className="td-cell text-sm">{r.mode === 'interactive' ? 'voice' : 'text'}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(r.requests)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(r.input_tokens)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(r.output_tokens)}</td>
                            <td className="td-cell text-right font-mono text-xs">{fmt(r.cached_tokens)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {rec.truncated && <p className="text-caption text-ui-text3">Showing the most recent runs only; the range has more than the aggregation limit.</p>}
              </div>
            ) : null}
          </section>
        </div>
      )}
    </PageShell>
  );
};

export default AiBillingPage;
